import './styles/ApplianceArchives.scss';

import { ImageBlock, TitleBanner } from '../components/Primitives';
import React, { useCallback, useEffect, useState } from 'react';

import ApplianceFilter from '../components/appliance/ApplianceFilter';
import ApplianceSingle from '../components/Listing/ApplianceSingle';
import { IAppliance } from '../interfaces';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import arrowIcon from '../images/icons/Icon-Fleche-Black.svg';
import arrowIconWhite from '../images/icons/Icon-Fleche-White.svg';
import { graphql } from 'gatsby';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductsPricesAndCode } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store/queries/useProductsPriceAndInventories';
import { useQueryArg } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useQueryArg';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query ($category: String!, $cmsPage: String!, $magento_id: [String!]) {
        allProductsJson(
            filter: { category: { regex: $category }, status: { eq: "actif" } }
            sort: { attributes: { size: { values: { en_CA: DESC } } } }
        ) {
            nodes {
                id
                code
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                category_localized {
                    en_CA
                    en_US
                    fr_CA
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    available_country {
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    PWMaSoi {
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    MaiMob {
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    Alcove {
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    Surfi {
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    EPA {
                        group
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    certifications {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    size {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
                images
                fields {
                    ...productsFieldsImagesFragment
                }
            }
        }
        cmsPage: allProductsJson(filter: { code: { eq: $cmsPage } }) {
            nodes {
                ...cmsDataFragment
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        magento_drolet_fr_ca {
            products(
                filter: { category_id: { in: $magento_id } }
                pageSize: 6500
                currentPage: 1
                sort: { relevance: ASC }
            ) {
                items {
                    sku
                }
            }
        }
        magento_drolet_en_us {
            products(
                filter: { category_id: { in: $magento_id } }
                pageSize: 6500
                currentPage: 1
                sort: { relevance: ASC }
            ) {
                items {
                    sku
                }
            }
        }
        superficieLink: allProductsJson(
            filter: { code: { eq: "drolet_superficie-lien" } }
        ) {
            nodes {
                code
                attributes {
                    texte_1 {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    link {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;
export interface IFilterDefaults {
    minVal: number;
    maxArea: number;
    maxPower: number;
}
const filterDefaults: IFilterDefaults = {
    minVal: 0,
    maxArea: 4000,
    maxPower: 120000,
};
export default function ApplianceArchives(props: {
    data: any;
    pageContext: any;
    location: any;
}) {
    const { data, pageContext, location } = props;
    const { pagePath, category } = pageContext;
    const allProducts = data.allProductsJson.nodes;
    const cmsPage = data?.cmsPage?.nodes[0];
    const metadataAttributes = cmsPage?.attributes;
    const prodPriceCp = [
        ...useProductsPricesAndCode(
            allProducts.map((family: { code: string }) => family.code)
        ),
    ];
   
    const { currentLocale } = usePageLocalization();

    const currentCountry = currentLocale.includes('CA')
        ? 'Canada'
        : 'United States';

    const prodWithPricesLocalized = allProducts
        ?.slice()
        .map((prod: any) => {
            const pPrice = prodPriceCp?.[0]?.find(
                (e: { sku: string | undefined }) => e.sku === prod.code
            );
           
            return {
                ...prod,
                ...pPrice,
            };
        })
        .filter((product: any) => {
            return product?.attributes?.available_country?.values?.some(
                (value: any) => {
                    return value?.[currentLocale] === currentCountry;
                }
            );
        });
     
    const [filteredProducts, setFilteredProducts] = useState(
        prodWithPricesLocalized
    );
    const metadata = {
        title: metadataAttributes?.meta_title?.values[0][currentLocale],
        description:
            metadataAttributes?.meta_description?.values[0][currentLocale],
    };
    const magentoSort =
        currentLocale === 'en_US'
            ? data?.magento_drolet_en_us
            : data?.magento_drolet_fr_ca;
    const { t } = useTranslation('layout');
    const isMobile = useMobileDetect().isMobile();
    const isTablet = useMobileDetect().isTablet();
    const isDesktop = useMobileDetect().isDesktop();
    const responsiveImages = cmsPage?.fields;
    const desktopImage =
        responsiveImages?.banniere_desktop?.[0]?.childImageSharp
            ?.gatsbyImageData;
    const mobileImage =
        responsiveImages?.banniere_mobile?.[0]?.childImageSharp
            ?.gatsbyImageData;

    const [priceArg] = useQueryArg('price');
    const [surfiArg] = useQueryArg('surfi');
    const [powerArg] = useQueryArg('power');
    const [installArg] = useQueryArg('install');
    const [sizeArg] = useQueryArg('size');
    const [madeInArg] = useQueryArg('madeIn');
    const [qPage] = useQueryArg('page');
    const [page, setPage] = useState(qPage ? parseInt(qPage) : 1);
    const [showSmallFilter, setShowSmallFilter] = useState(true);
    const [sort, setSort] = useQueryArg('sort');
    const powerArgArr = powerArg?.split('-');
    const [filterPowerState, setFilterPowerState] = useState(
        powerArg
            ? [
                  parseInt(
                      powerArgArr?.[0] ?? filterDefaults.minVal.toString()
                  ),
                  parseInt(
                      powerArgArr?.[1] ?? filterDefaults.maxPower.toString()
                  ),
              ]
            : [filterDefaults.minVal, filterDefaults.maxPower]
    );

    const surfiArgArr = surfiArg?.split('-');
    const [filterSurfiState, setFilterSurfiState] = useState(
        surfiArg
            ? [
                  parseInt(
                      surfiArgArr?.[0] ?? filterDefaults.minVal.toString()
                  ),
                  parseInt(
                      surfiArgArr?.[1] ?? filterDefaults.maxArea.toString()
                  ),
              ]
            : [filterDefaults.minVal, filterDefaults.maxArea]
    );

    const [filterSizeState, setFilterSizeState] = useState(sizeArg || '');
    const [filterMadeInState, setFilterMadeInState] = useState(madeInArg || '');
    const [filterPriceState, setFilterPriceState] = useState(priceArg || '');
    const [filterInstallState, setFilterInstallState] = useState(
        installArg || ''
    );

    if (location && typeof window !== 'undefined') {
        window.scrollTo(0, location?.state?.scrollPosition);
    }
    useEffect(() => {
        if (priceArg) {
            setFilterPriceState(priceArg);
        } else {
            setFilterPriceState('');
        }
        if (installArg) {
            setFilterInstallState(installArg);
        } else {
            setFilterInstallState('');
        }

        if (powerArg) {
            try {
                const args = powerArg?.split('-');
                setFilterPowerState([parseInt(args[0]), parseInt(args[1])]);
            } catch (e) {
                console.error(e);
            }
        } else {
            setFilterPowerState([
                filterDefaults.minVal,
                filterDefaults.maxPower,
            ]);
        }

        if (surfiArg) {
            try {
                const args = surfiArg?.split('-');
                setFilterSurfiState([parseInt(args[0]), parseInt(args[1])]);
            } catch (e) {
                console.error(e);
            }
        } else {
            setFilterSurfiState([
                filterDefaults.minVal,
                filterDefaults.maxArea,
            ]);
        }

        if (sizeArg) {
            setFilterSizeState(sizeArg);
        } else {
            setFilterSizeState('');
        }

        if (madeInArg) {
            setFilterMadeInState(madeInArg);
        } else {
            setFilterMadeInState('');
        }
        
        setPage(page);

    }, [installArg, page, powerArg, priceArg, sizeArg, surfiArg, madeInArg]);

    useEffect(() => {
        if (isMobile) {
            setShowSmallFilter(false);
        }
        if (isTablet || isDesktop) {
            setShowSmallFilter(true);
        }
    }, [isDesktop, isMobile, isTablet]);

    return (
        <Layout>
            <SEO
                title={metadata?.title}
                description={metadata?.description}
                isDescriptionCurrentLocale={true}
            />
            <TitleBanner
                className='appliance-listing-titlebanner'
                imageData={isMobile ? mobileImage : desktopImage}
                title={cmsPage?.title?.[currentLocale]}
            />

            <div className='appliance-archive-page'>
                <div
                    className='small-filter-btn'
                    onClick={() => {
                        setShowSmallFilter(!showSmallFilter);
                    }}
                >
                    <span>{t('venting.filter')}</span>
                    <img
                        className={`iconRight`}
                        src={arrowIconWhite}
                        alt={''}
                        style={
                            showSmallFilter
                                ? { transform: 'rotate(90deg)' }
                                : undefined
                        }
                    />
                </div>

                {showSmallFilter && (
                    <ApplianceFilter
                        filteredProducts={filteredProducts}
                        allProducts={prodWithPricesLocalized}
                        setFilteredProducts={setFilteredProducts}
                        filterDefaults={filterDefaults}
                        path={
                            sort ? `${pagePath}?sort=${sort}&` : `${pagePath}?`
                        }
                        filterSurfiState={filterSurfiState}
                        filterPowerState={filterPowerState}
                        filterPriceState={filterPriceState}
                        filterInstallState={filterInstallState}
                        filterSizeState={filterSizeState}
                        filterMadeInState={filterMadeInState}
                        powerArg={powerArg}
                        surfiArg={surfiArg}
                        priceArg={priceArg}
                        installArg={installArg}
                        sizeArg={sizeArg}
                        madeInArg={madeInArg}
                        applianceCategory={category}
                        surfiLink={data.superficieLink?.nodes?.[0]?.attributes}
                        setPage={setPage}
                    />
                )}

                <div className='archive-listing-container'>
                    <div className='appliance-second-filter-container'>
                        <ApplianceSecondFilter
                            filteredProducts={filteredProducts}
                            setFilteredProducts={setFilteredProducts}
                            setPage={setPage}
                            priceAndCode={prodPriceCp}
                            powerArg={powerArg}
                            surfiArg={surfiArg}
                            priceArg={priceArg}
                            installArg={installArg}
                            magentoResult={magentoSort}
                            setSort={setSort}
                            sort={sort}
                        />
                    </div>
                    <div className='archive-listing'>
                        <span className='results'>
                            {filteredProducts.length}{' '}
                            {t('pages:archives.results')}
                        </span>

                        {filteredProducts?.map(
                            (appliance: IAppliance, index: number) => (
                                <ApplianceSingle
                                    appliance={appliance}
                                    key={`${appliance.code}-archive-listing-${index}`}
                                />
                            )
                        )}
                    </div>
                </div>
            </div>
            <ImageBlock
                subtitle={
                    cmsPage?.attributes?.texte_1?.values?.[0]?.[currentLocale]
                }
                title={
                    cmsPage?.attributes?.texte_2?.values?.[0]?.[currentLocale]
                }
                image={
                    cmsPage?.fields?.images?.[0]?.childImageSharp
                        ?.gatsbyImageData
                }
                content={
                    cmsPage?.attributes?.content?.values?.[0]?.[currentLocale]
                }
            />
        </Layout>
    );
}

function ApplianceSecondFilter(props: {
    filteredProducts: IAppliance[];
    setFilteredProducts: React.Dispatch<IAppliance[]>;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    priceAndCode: any[];
    powerArg: string;
    surfiArg: string;
    priceArg: string;
    installArg: string;
    magentoResult: any;
    setSort: (value: string) => void;
    sort: string;
}) {
    const {
        filteredProducts,
        setFilteredProducts,
        setPage,
        installArg,
        powerArg,
        priceArg,
        surfiArg,
        magentoResult,
        setSort,
        sort,
    } = props;
    const [showFilter, setShowFilter] = useState(false);
    const { t } = useTranslation('layout');
    const [currentFilterType, setCurrentFilterType]: [
        string,
        React.Dispatch<React.SetStateAction<string>>
    ] = useState(t('appliance.filter.size'));
    const { currentLocale } = usePageLocalization();
    const cleanUp = useCallback(
        (filterTitle: string) => {
            setCurrentFilterType(filterTitle);
            setShowFilter(false);
            setPage(1);
        },
        [setPage]
    );
    const updateSort = useCallback(() => {
        let sortedProd = [...filteredProducts];

        switch (sort) {
            case 'name':
                sortedProd = sortedProd.sort((prodA, prodB) => {
                    return prodA?.title?.[currentLocale]?.localeCompare(
                        prodB?.title?.[currentLocale]
                    );
                });
                break;
            case 'price-low':
                sortedProd = sortedProd.sort((prodA, prodB) => {
                    if (
                        prodA.price?.finalPrice?.value <
                        prodB.price?.finalPrice?.value
                    ) {
                        return -1;
                    } else if (
                        prodA.price?.finalPrice?.value >
                        prodB.price?.finalPrice?.value
                    ) {
                        return 1;
                    } else {
                        return prodA.code.localeCompare(prodB.code);
                    }
                });
                break;
            case 'price-high':
                sortedProd = sortedProd.sort((prodA, prodB) => {
                    if (
                        prodA.price?.finalPrice?.value >
                        prodB.price?.finalPrice?.value
                    ) {
                        return -1;
                    } else if (
                        prodA.price?.finalPrice?.value <
                        prodB.price?.finalPrice?.value
                    ) {
                        return 1;
                    } else {
                        return prodA.code.localeCompare(prodB.code);
                    }
                });
                break;
            case 'popularity':
                sortedProd = sortedProd.sort(
                    (prodA, prodB) =>
                        magentoResult?.products?.items?.findIndex(
                            (e: { sku: string }) => e?.sku === prodA?.code
                        ) -
                            magentoResult?.products?.items?.findIndex(
                                (e: { sku: string }) => e?.sku === prodB?.code
                            ) || 0
                );
                break;
            default:
                sortedProd = sortedProd.sort((prodA, prodB) => {
                    const sizeCompareResult: number =
                        prodB?.attributes?.size?.values?.[0]?.[
                            'en_CA'
                        ]?.localeCompare(
                            prodA?.attributes?.size?.values?.[0]?.['en_CA']
                        );

                    if (sizeCompareResult === 0) {
                        return prodA.code.localeCompare(prodB.code);
                    }

                    return sizeCompareResult;
                });
                break;
        }

        setFilteredProducts(sortedProd);
    }, [
        currentLocale,
        filteredProducts,
        magentoResult?.products?.items,
        setFilteredProducts,
        sort,
    ]);
    useEffect(() => {
        if (!sort) {
            setCurrentFilterType(currentLocale === 'fr_CA' ? 'Taille' : 'Size');
        }

        updateSort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installArg, powerArg, priceArg, surfiArg, currentLocale, sort]);

    useEffect(() => {
        if (sort === '') {
            cleanUp(t(`appliance.filter.size`));
        } else {
            cleanUp(t(`appliance.filter.${sort}`));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort]);

    const normal = 'second-filter-elem';
    const selected = 'second-filter-elem filter-selected';
    const getClassName = (val: string) => {
        if (val === currentFilterType) return selected;
        return normal;
    };
    return (
        <div
            className='appliance-second-filter'
            onClick={() => setShowFilter(!showFilter)}
        >
            <div className='second-filter-title'>
                <span>{t('appliance.filter.sort-by') + currentFilterType}</span>
                <img
                    className={`iconRight`}
                    src={arrowIcon}
                    alt={''}
                    style={showFilter ? { transform: 'rotate(90deg)' } : {}}
                />
            </div>
            <div
                className='appliance-filter-list'
                style={{ display: showFilter ? 'block' : 'none' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className={getClassName(t('appliance.filter.name'))}
                    onClick={() => {
                        setSort('name');
                    }}
                >
                    <span>{t('appliance.filter.name')}</span>
                </div>
                <div
                    className={getClassName(t('appliance.filter.size'))}
                    onClick={() => setSort('size')}
                >
                    <span>{t('appliance.filter.size')}</span>
                </div>
                <div
                    className={getClassName(t('appliance.filter.price-low'))}
                    onClick={() => setSort('price-low')}
                >
                    <span>{t('appliance.filter.price-low')}</span>
                </div>
                <div
                    className={getClassName(t('appliance.filter.price-high'))}
                    onClick={() => setSort('price-high')}
                >
                    <span>{t('appliance.filter.price-high')}</span>
                </div>
                <div
                    className={getClassName(t('appliance.filter.popularity'))}
                    onClick={() => setSort('popularity')}
                >
                    <span>{t('appliance.filter.popularity')}</span>
                </div>
            </div>
        </div>
    );
}
